<script setup lang="ts">
import type { FormInstance, FormRules } from 'element-plus'
import { Message as IconMessage } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { computed, onBeforeUnmount, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  useCheckEmailMutation,
  usePasswordResetMutation,
} from '@/entities/auth'
import { useEmailOtpSendMutation } from '@/entities/emailOtp'
import { useLoginDialog } from '@/shared/composables/useLoginDialog'
import CDialog from '@/shared/ui/CDialog.vue'
import 'element-plus/theme-chalk/el-message.css'

interface Emits {
  (e: 'back'): void
}

const emit = defineEmits<Emits>()

const { t } = useI18n()

enum Steps {
  Email = 1,
  Confirmation = 2,
  Password = 3,
}

const activeStep = ref(Steps.Email)

const TITLES = {
  [Steps.Email]: 'restore_password',
  [Steps.Confirmation]: 'enter_email_code',
  [Steps.Password]: 'set_new_password',
}

const activeTitle = computed(() => TITLES[activeStep.value])

const showForgetPasswordDialog = defineModel<boolean>({ default: true })
const isSubmitting = ref(false)

// Form refs
const emailFormRef = ref<FormInstance>()
const smsFormRef = ref<FormInstance>()
const passwordFormRef = ref<FormInstance>()

// Email form
const emailForm = ref({
  email: '',
})

// SMS form
const smsForm = ref({
  smsCode: '',
})

// Password form
const passwordForm = ref({
  password: '',
})

// Validation rules
const emailRules: FormRules = {
  email: [
    { required: true, message: t('email_required'), trigger: 'blur' },
    { type: 'email', message: t('invalid_email_format'), trigger: 'blur' },
  ],
}

const smsRules: FormRules = {
  smsCode: [
    { required: true, message: t('email_code_required'), trigger: 'blur' },
  ],
}

// Password validation function
function validatePassword(
  _rule: unknown,
  value: string,
  callback: (error?: Error) => void,
) {
  if (!value) {
    callback(new Error(t('password_required')))
    return
  }

  // Должно быть минимум 8 символов
  if (value.length < 8) {
    callback(new Error('Должно быть минимум 8 символов'))
    return
  }

  // Должна быть хотя бы одна буква (A–Z или a–z)
  if (!/[a-z]/i.test(value)) {
    callback(new Error('Должна быть хотя бы одна буква (A–Z или a–z)'))
    return
  }

  // Должна быть хотя бы одна цифра (0–9)
  if (!/\d/.test(value)) {
    callback(new Error('Должна быть хотя бы одна цифра (0–9)'))
    return
  }

  // Не должно состоять только из цифр
  if (/^\d+$/.test(value)) {
    callback(new Error('Не должно состоять только из цифр'))
    return
  }

  callback()
}

const passwordRules: FormRules = {
  password: [
    { required: true, message: t('password_required'), trigger: 'blur' },
    { validator: validatePassword, trigger: 'blur' },
  ],
}

const countdownSeconds = ref(60)
const countdownInterval = ref()

function formatTime(seconds: number) {
  let minutes = String(Math.floor(seconds / 60))
  let remainingSeconds = String(seconds % 60)

  if (Number(remainingSeconds) < 10) {
    remainingSeconds = `0${remainingSeconds}`
  }

  if (Number(minutes) < 10) {
    minutes = `0${minutes}`
  }

  return `${minutes}:${remainingSeconds}`
}

const countdownText = computed(() => {
  const time = formatTime(countdownSeconds.value)
  return countdownSeconds.value > 0
    ? t('get_code_again', { time })
    : t('get_code')
})

const isCountdownActive = computed(() => countdownSeconds.value > 0)

function startCountdown() {
  countdownSeconds.value = 60
  countdownInterval.value = setInterval(() => {
    countdownSeconds.value--
    if (countdownSeconds.value <= 0) {
      clearInterval(countdownInterval.value)
    }
  }, 1000)
}

const { mutate: checkEmail } = useCheckEmailMutation()
const { mutate: sendEmailOtp } = useEmailOtpSendMutation()
const { mutate: resetPassword } = usePasswordResetMutation()
const { onBackToLogin } = useLoginDialog()

async function onGetCode() {
  if (!emailFormRef.value) 
    return

  try {
    await emailFormRef.value.validate()
    isSubmitting.value = true

    // First check if email exists
    checkEmail(emailForm.value.email, {
      onSuccess: async (response) => {
        // Check if email exists in response
        if (response.exists) {
          // If email exists, send OTP
          sendEmailOtp(
            { email: emailForm.value.email },
            {
              onSuccess: () => {
                activeStep.value = Steps.Confirmation
                startCountdown()
                ElMessage.success(t('code_sent_successfully'))
                isSubmitting.value = false
              },
              onError: (error) => {
                console.error('Failed to send OTP:', error)
                ElMessage.error(t('failed_to_send_code'))
                isSubmitting.value = false
              },
            },
          )
        }
        else {
          // Email doesn't exist
          ElMessage.error(t('email_not_exists'))
          isSubmitting.value = false
        }
      },
      onError: (error) => {
        console.error('Email check failed:', error)
        ElMessage.error(t('email_check_failed'))
        isSubmitting.value = false
      },
    })
  }
  catch (error) {
    console.error('Form validation failed:', error)
    isSubmitting.value = false
  }
}

async function onGetCodeAgain() {
  try {
    isSubmitting.value = true

    // Send OTP again without email validation
    sendEmailOtp(
      { email: emailForm.value.email },
      {
        onSuccess: () => {
          startCountdown()
          ElMessage.success(t('code_sent_successfully'))
          isSubmitting.value = false
        },
        onError: (error) => {
          console.error('Failed to send OTP:', error)
          ElMessage.error(t('failed_to_send_code'))
          isSubmitting.value = false
        },
      },
    )
  }
  catch (error) {
    console.error('Failed to send OTP again:', error)
    ElMessage.error(t('failed_to_send_code'))
    isSubmitting.value = false
  }
}

async function onConfirmSmsCode() {
  if (!smsFormRef.value) 
    return

  try {
    await smsFormRef.value.validate()
    isSubmitting.value = true

    // TODO: Implement SMS code verification
    // await verifySmsCode(smsForm.value.smsCode, {
    //   onSuccess: () => {
    //     activeStep.value = Steps.Password
    //   }
    // })

    // For now, just move to next step
    activeStep.value = Steps.Password
  }
  catch (error) {
    console.error('SMS form validation failed:', error)
  }
  finally {
    isSubmitting.value = false
  }
}

async function onConfirmPassword() {
  if (!passwordFormRef.value) 
    return

  try {
    await passwordFormRef.value.validate()
    isSubmitting.value = true

    resetPassword(
      {
        email: emailForm.value.email,
        otp: Number(smsForm.value.smsCode),
        new_password: passwordForm.value.password,
      },
      {
        onSuccess: () => {
          ElMessage.success(t('password_reset_successful_uz'))
          onBackToLogin()
          isSubmitting.value = false
        },
        onError: (error) => {
          console.error('Password reset failed:', error)
          ElMessage.error(
            t('password_reset_failed') || 'Parolni tiklashda xatolik yuz berdi',
          )
          isSubmitting.value = false
        },
      },
    )
  }
  catch (error) {
    console.error('Password form validation failed:', error)
    isSubmitting.value = false
  }
}

onBeforeUnmount(() => {
  clearInterval(countdownInterval.value)
})

function handleBack() {
  if (activeStep.value === Steps.Confirmation) {
    activeStep.value = Steps.Email
  }
  else {
    emit('back')
  }
}
</script>

<template>
  <CDialog v-model="showForgetPasswordDialog" class="sm:!w-[480px] !w-[90%]">
    <template #header>
      <h4 class="flex items-center gap-2">
        <ElButton v-if="activeStep !== Steps.Password" link @click="handleBack">
          <ElIcon><ElIconArrowLeft /></ElIcon>
        </ElButton>
        <Transition name="fade" mode="out-in">
          <span :key="activeStep">{{ $t(activeTitle) }}</span>
        </Transition>
      </h4>
    </template>

    <Transition name="fade" mode="out-in">
      <ElText v-if="activeStep === Steps.Confirmation" tag="p" class="!mb-3">
        {{ $t("enter_email_code_description") }}
      </ElText>
    </Transition>

    <Transition name="fade" mode="out-in">
      <!-- Email Form -->
      <ElForm
        v-if="activeStep === Steps.Email"
        ref="emailFormRef"
        :model="emailForm"
        :rules="emailRules"
        label-position="top"
        @submit.prevent="onGetCode"
      >
        <ElFormItem prop="email" :label="$t('email')">
          <ElInput v-model="emailForm.email" :placeholder="$t('enter_email')" />
        </ElFormItem>
      </ElForm>

      <!-- SMS Code Form -->
      <ElForm
        v-else-if="activeStep === Steps.Confirmation"
        ref="smsFormRef"
        :model="smsForm"
        :rules="smsRules"
        label-position="top"
        @submit.prevent="onConfirmSmsCode"
      >
        <ElFormItem prop="smsCode" :label="$t('enter_code')">
          <ElInput v-model="smsForm.smsCode" :placeholder="$t('enter')" />
        </ElFormItem>
      </ElForm>

      <!-- Password Form -->
      <ElForm
        v-else
        ref="passwordFormRef"
        :model="passwordForm"
        :rules="passwordRules"
        label-position="top"
        @submit.prevent="onConfirmPassword"
      >
        <ElFormItem prop="password" :label="$t('password')">
          <ElInput
            v-model="passwordForm.password"
            type="password"
            :placeholder="$t('enter_password')"
            show-password
          />
        </ElFormItem>
      </ElForm>
    </Transition>

    <template #footer>
      <Transition name="fade" mode="out-in">
        <div :key="activeStep" class="flex items-center justify-end">
          <!-- Get Code Button -->
          <ElButton
            v-if="activeStep === Steps.Email"
            type="primary"
            :icon="IconMessage"
            :loading="isSubmitting"
            @click="onGetCode"
          >
            {{ $t("get_code") }}
          </ElButton>

          <!-- Get Code Again Button -->
          <ElButton
            v-if="activeStep === Steps.Confirmation"
            :icon="IconMessage"
            type="primary"
            :disabled="isCountdownActive"
            :loading="isSubmitting"
            @click="onGetCodeAgain"
          >
            {{ countdownText }}
          </ElButton>

          <!-- Confirm SMS Code Button -->
          <ElButton
            v-if="activeStep === Steps.Confirmation"
            type="primary"
            :loading="isSubmitting"
            @click="onConfirmSmsCode"
          >
            {{ $t("confirm") }}
          </ElButton>

          <!-- Confirm Password Button -->
          <ElButton
            v-if="activeStep === Steps.Password"
            type="primary"
            :loading="isSubmitting"
            @click="onConfirmPassword"
          >
            {{ $t("confirm") }}
          </ElButton>
        </div>
      </Transition>
    </template>
  </CDialog>
</template>
